import { useLocation } from "wouter";
import { Activity, RefreshCw } from "lucide-react";
import { trpc } from "@/lib/trpc";
import type { RouterOutputs } from "@/lib/trpc";
import { useI18n } from "@/lib/i18n";
import { PlatformNav } from "@/components/platform/PlatformNav";

/**
 * ADMIN HEALTH CENTER — platform-tier observability.
 *
 * Answers, on one screen: is Receipt healthy, what is degraded, is AI working,
 * and what needs an administrator. Nothing more.
 *
 * OBSERVABILITY ONLY. There is no control on this page — no restart, no retry,
 * no migration, no key rotation, no cache clear. A status page that can change
 * production is a different, riskier product, and this is not it. (The older
 * org-tier `/admin/health` page does carry job triggers; that surface and its
 * authority are deliberately untouched by this one.)
 *
 * THE SERVER SENDS CODES, THIS FILE SENDS WORDS. Every status, issue and
 * feature arrives as a stable enum, so the page renders in English and Arabic
 * without the server composing sentences it cannot localize — and so that
 * provider prose has no path to the screen even by accident.
 *
 * HONESTY OVER COMPLETENESS. Counters are labelled "since restart" because they
 * are process-local and reset on deploy. Metrics Receipt genuinely cannot
 * produce are listed as unavailable rather than approximated: a plausible wrong
 * number on a health page is worse than an absent one.
 *
 * NOT COLOUR ALONE. Every status carries a word; colour is an accent.
 */

type Level = "healthy" | "degraded" | "critical";
type Status =
  | "operational" | "degraded" | "critical"
  | "not_configured" | "no_data" | "pilot_locked";

const STATUS_TEXT: Record<Status, [string, string]> = {
  operational: ["Operational", "يعمل"],
  degraded: ["Degraded", "متدهور"],
  critical: ["Critical", "حرج"],
  not_configured: ["Not configured", "غير مُهيّأ"],
  no_data: ["No data yet", "لا توجد بيانات بعد"],
  pilot_locked: ["Pilot locked", "مقفل تجريبياً"],
};

const OVERALL_TEXT: Record<Level, [string, string]> = {
  healthy: ["Healthy", "سليم"],
  degraded: ["Degraded", "متدهور"],
  critical: ["Critical", "حرج"],
};

/** Operator-facing wording for every issue the server can raise. */
const ISSUE_TEXT: Record<string, [string, string]> = {
  CREDIT_UNAVAILABLE: [
    "AI requests are rejected because provider quota or credit is unavailable.",
    "تُرفض طلبات الذكاء الاصطناعي لعدم توفّر رصيد أو حصة لدى المزوّد.",
  ],
  RATE_LIMITED: ["The AI provider is rate limiting requests.", "يحدّ مزوّد الذكاء الاصطناعي من معدّل الطلبات."],
  PROVIDER_AUTH_ERROR: [
    "The AI provider rejected the configured credentials.",
    "رفض مزوّد الذكاء الاصطناعي بيانات الاعتماد المُهيّأة.",
  ],
  PROVIDER_BAD_REQUEST: ["The AI provider rejected the request as invalid.", "رفض المزوّد الطلب لعدم صلاحيته."],
  PROVIDER_UNAVAILABLE: ["The AI provider is returning server errors.", "يُرجع المزوّد أخطاء خادم."],
  TIMEOUT: ["AI requests are exceeding their deadline.", "تتجاوز طلبات الذكاء الاصطناعي المهلة."],
  TRANSPORT: ["AI requests fail before a provider response.", "تفشل الطلبات قبل ورود ردّ المزوّد."],
  BUDGET_EXHAUSTED: [
    "Receipt's own AI call budget for this period is exhausted.",
    "استُنفدت ميزانية استدعاءات الذكاء الاصطناعي الخاصة بـ Receipt لهذه الفترة.",
  ],
  AI_NOT_CONFIGURED: ["No AI provider is configured.", "لم يُهيّأ أي مزوّد ذكاء اصطناعي."],
  DB_UNREACHABLE: ["The database is unreachable.", "تعذّر الوصول إلى قاعدة البيانات."],
  DB_LATENCY_CRITICAL: ["Database latency is critical.", "زمن استجابة قاعدة البيانات حرج."],
  DB_LATENCY_HIGH: ["Database latency is high.", "زمن استجابة قاعدة البيانات مرتفع."],
  MIGRATIONS_PENDING: ["Schema migrations are pending.", "توجد ترحيلات مخطط معلّقة."],
};

const FEATURE_TEXT: Record<string, [string, string]> = {
  your_week_reflection: ["Your Week reflection", "تأمّل أسبوعك"],
  daily_receipt_draft: ["Daily receipt draft", "مسودة الإيصال اليومي"],
  weekly_receipt_draft: ["Weekly receipt draft", "مسودة الإيصال الأسبوعي"],
  weekly_team_report: ["Weekly team report", "تقرير الفريق الأسبوعي"],
  ask_ai: ["Ask AI", "اسأل الذكاء الاصطناعي"],
  team_ask: ["Team ask", "سؤال الفريق"],
  photo_processing: ["Photo processing", "معالجة الصور"],
  voice_processing: ["Voice processing", "معالجة الصوت"],
  department_report: ["Department report", "تقرير القسم"],
  weekly_retrospective: ["Weekly retrospective", "المراجعة الأسبوعية"],
  growth_review: ["Growth review", "مراجعة التطور"],
  one_on_one_prep: ["1:1 prep", "تحضير اللقاء الثنائي"],
  // Not a feature: the honest bucket for a provider call that arrived without a
  // server-authored id. Visible so it can be fixed, never silently absorbed.
  unattributed: ["Unattributed calls", "استدعاءات غير منسوبة"],
};

const PROVIDER_TEXT: Record<string, [string, string]> = {
  openai: ["OpenAI", "OpenAI"],
  anthropic: ["Anthropic", "Anthropic"],
};

/**
 * The unions come from the SERVER DTO, inferred through tRPC — not re-typed by
 * hand. `satisfies Record<Union, …>` then makes a missing localization a
 * COMPILE error: adding a status or a lifecycle server-side without adding copy
 * here fails the build instead of shipping a raw snake_case enum to an
 * administrator, in Arabic as well as English. That is exactly the miss this
 * page shipped once already, when `api_only` was added to the server union.
 */
type AiOps = RouterOutputs["platform"]["healthOverview"]["aiOperations"];
type OpStatus = AiOps["features"][number]["status"];
type Lifecycle = AiOps["features"][number]["lifecycle"];

const OPSTATUS_TEXT = {
  healthy: ["Healthy", "سليم"],
  degraded: ["Degraded", "متدهور"],
  no_data: ["No data", "لا توجد بيانات"],
  not_configured: ["Not configured", "غير مُهيّأ"],
  api_only: ["API only", "عبر الواجهة فقط"],
  dormant: ["Dormant", "خامل"],
  pilot_locked: ["Pilot locked", "مقفلة أثناء التجربة"],
} satisfies Record<OpStatus, [string, string]>;

/**
 * LIFECYCLE is a different question from operational status, and both belong on
 * the row. "Is this surface part of the product?" and "is it working right now?"
 * have independent answers: `ask_ai` is reachable by any authenticated caller
 * with no mounted UI, so "Degraded" alone would hide why it is surprising that
 * it has traffic at all.
 */
const LIFECYCLE_TEXT = {
  live: ["Live", "مفعّلة"],
  api_only: ["API only", "عبر الواجهة فقط"],
  dormant: ["Dormant", "خامل"],
  pilot_locked: ["Pilot locked", "مقفلة أثناء التجربة"],
} satisfies Record<Lifecycle, [string, string]>;

/**
 * Safe lookup. Types make a miss impossible at compile time, but the transport
 * is JSON from a running server that may be a version ahead during a deploy, so
 * an unknown value must degrade to localized words — never echo the raw enum.
 */
const UNAVAILABLE_LABEL: [string, string] = ["Unavailable", "غير متاح"];

/**
 * The server's `status` field carries a LIFECYCLE value when an `api_only` (or
 * `dormant`) feature has no traffic yet — `AiOpStatus` is a union of two ideas,
 * operational states and lifecycle echoes. That was invisible while the page
 * rendered status alone; once lifecycle got its own element the echo showed up
 * as "API only · API only", saying the same thing twice and leaving the
 * operational question unanswered.
 *
 * A lifecycle value in the status slot means exactly "nothing operational to
 * report yet", which is `no_data`. Translating it here keeps the two columns
 * independent without touching the DTO or the server. Tidying the server union
 * so it only ever carries operational states is the better long-term shape and
 * is recorded as follow-up.
 */
const LIFECYCLE_ECHO: ReadonlySet<string> = new Set<string>(["api_only", "dormant"]);
const operationalStatus = (status: OpStatus): OpStatus =>
  LIFECYCLE_ECHO.has(status) ? "no_data" : status;

const UNAVAILABLE_TEXT: Record<string, [string, string]> = {
  ai_per_feature_attribution: ["AI usage per feature", "استخدام الذكاء الاصطناعي لكل ميزة"],
  ai_latency: ["AI latency", "زمن استجابة الذكاء الاصطناعي"],
  ai_token_totals: ["AI token totals", "إجمالي وحدات الذكاء الاصطناعي"],
  ai_calls_surviving_restart: ["AI calls across restarts", "الاستدعاءات عبر عمليات إعادة التشغيل"],
  scheduled_job_run_history: ["Scheduled job history", "سجل المهام المجدولة"],
  integration_failure_rates: ["Integration failure rates", "معدلات فشل التكاملات"],
  notification_delivery_outcomes: ["Notification delivery", "تسليم الإشعارات"],
  backup_freshness: ["Backup freshness", "حداثة النسخ الاحتياطي"],
  build_commit_identifier: ["Build identifier", "معرّف الإصدار"],
};

const ACCENT: Record<string, string> = {
  operational: "text-emerald-600",
  healthy: "text-emerald-600",
  degraded: "text-amber-600",
  critical: "text-red-600",
  not_configured: "text-muted-foreground",
  no_data: "text-muted-foreground",
  pilot_locked: "text-muted-foreground",
  dormant: "text-muted-foreground",
  api_only: "text-muted-foreground",
};

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-3xl border border-border/60 bg-card p-4 shadow-sm">
      <h2 className="mb-2 text-sm font-semibold text-foreground">{title}</h2>
      {children}
    </section>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="min-w-0 break-words text-end font-medium">{value}</span>
    </div>
  );
}

export default function AdminHealthCenter() {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const t = (pair: [string, string] | undefined, fallback = "—") => (pair ? (isAr ? pair[1] : pair[0]) : fallback);
  /** Never echoes a machine value: an unmapped key renders localized words. */
  const label = (map: Record<string, [string, string]>, key: string) =>
    t((map as Record<string, [string, string]>)[key] ?? UNAVAILABLE_LABEL);
  const [, navigate] = useLocation();

  const meQ = trpc.org.me.useQuery(undefined, { staleTime: 60_000 });
  // MANUAL refresh by default: a status page that polls hard is a load source of
  // its own, and nothing here changes second to second.
  const q = trpc.platform.healthOverview.useQuery(undefined, {
    enabled: meQ.data?.platformAdmin === true,
    staleTime: 15_000,
  });

  // Server already forbids the data; this only avoids a broken-looking page.
  if (meQ.isSuccess && !meQ.data?.platformAdmin) {
    navigate("/");
    return null;
  }

  const d = q.data;
  const when = (ms: number | null) =>
    ms == null ? t(["Never", "أبداً"]) : new Date(ms).toLocaleString(isAr ? "ar" : "en");

  return (
    <div className="min-h-screen bg-background">
      <main className="mx-auto w-full max-w-2xl px-4 py-4 space-y-3" data-testid="admin-health-center">
        <header className="flex items-center justify-between gap-3">
          <h1 className="flex items-center gap-2 font-display text-2xl">
            <Activity className="h-5 w-5 text-coral" />
            {isAr ? "مركز صحة النظام" : "Admin Health"}
          </h1>
          <button
            type="button"
            data-testid="admin-health-refresh"
            onClick={() => q.refetch()}
            disabled={q.isFetching}
            className="inline-flex items-center gap-1.5 rounded-full border border-border/60 px-3 py-1 text-xs hover:bg-accent/50 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${q.isFetching ? "animate-spin" : ""}`} />
            {isAr ? "تحديث" : "Refresh"}
          </button>
        </header>

        <PlatformNav />

        {q.isLoading ? (
          <p className="text-sm text-muted-foreground">{isAr ? "جارٍ التحميل…" : "Loading…"}</p>
        ) : !d ? (
          <p className="text-sm text-muted-foreground" data-testid="admin-health-unavailable">
            {isAr ? "تعذّر تحميل حالة النظام." : "Couldn’t load system health."}
          </p>
        ) : (
          <>
            <Card title={isAr ? "الحالة العامة" : "Overall"}>
              <p className={`text-lg font-semibold ${ACCENT[d.overall]}`} data-testid="admin-health-overall">
                {t(OVERALL_TEXT[d.overall as Level])}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {isAr ? "حُسبت في " : "Calculated "}
                {when(d.generatedAt)}
              </p>
            </Card>

            <Card title={isAr ? "ما يحتاج انتباهاً" : "Active issues"}>
              {d.issues.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="admin-health-no-issues">
                  {isAr ? "لا توجد مشكلات." : "No active issues."}
                </p>
              ) : (
                <ul className="space-y-2">
                  {d.issues.map((i, k) => (
                    <li key={k} data-testid="admin-health-issue" className="text-sm">
                      <span className={`font-medium ${ACCENT[i.severity]}`}>
                        {t(OVERALL_TEXT[i.severity as Level])}
                      </span>
                      <span className="text-muted-foreground"> · {i.subsystem} · </span>
                      <span className="font-mono text-xs">{i.code}</span>
                      <p className="min-w-0 break-words text-muted-foreground">{t(ISSUE_TEXT[i.code])}</p>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card title={isAr ? "الذكاء الاصطناعي والمزوّد" : "AI & provider"}>
              <Row label={isAr ? "الحالة" : "Status"} value={
                <span className={ACCENT[d.ai.status]} data-testid="admin-health-ai-status">
                  {t(STATUS_TEXT[d.ai.status as Status])}
                </span>
              } />
              <Row label={isAr ? "المزوّد" : "Endpoint"} value={d.ai.endpointHost ?? "—"} />
              <Row label={isAr ? "النموذج" : "Model"} value={d.ai.model ?? "—"} />
              <Row label={isAr ? "آخر نجاح" : "Last success"} value={when(d.ai.lastSuccessAt)} />
              <Row label={isAr ? "آخر فشل" : "Last failure"} value={when(d.ai.lastFailureAt)} />
              <Row
                label={isAr ? "ميزانية Receipt اليوم" : "Receipt budget (day)"}
                value={`${d.ai.budget.dayUsed} / ${d.ai.budget.dayCap}`}
              />
              <p className="mt-2 text-xs text-muted-foreground" data-testid="admin-health-scope-note">
                {isAr
                  ? "تُحسب هذه الأرقام منذ آخر تشغيل للعملية فقط، وتُصفَّر عند إعادة النشر. وميزانية Receipt ليست رصيد المزوّد. ولا تشمل هذه الميزانية استدعاءات Anthropic، فهي ليست إجمالي استخدام الذكاء الاصطناعي."
                  : "These counts are since this process started and reset on deploy. Receipt's budget is not the provider's credit balance. It also excludes Anthropic calls, so it is not total AI usage."}
              </p>
            </Card>

            <Card title={isAr ? "التطبيق وقاعدة البيانات" : "Application & database"}>
              <Row label={isAr ? "قاعدة البيانات" : "Database"} value={
                <span className={ACCENT[d.database.status]}>
                  {t(STATUS_TEXT[d.database.status as Status])}
                  {d.database.latencyMs != null ? ` · ${d.database.latencyMs} ms` : ""}
                </span>
              } />
              <Row label={isAr ? "الترحيلات" : "Migrations"} value={
                <span className={ACCENT[d.migrations.status]}>
                  {t(STATUS_TEXT[d.migrations.status as Status])}
                  {d.migrations.pending != null ? ` · ${d.migrations.pending}` : ""}
                </span>
              } />
              <Row label={isAr ? "مدة التشغيل" : "Uptime"} value={`${Math.round(d.application.uptimeSec / 3600)} h`} />
              <Row label={isAr ? "الذاكرة" : "Memory"} value={`${d.application.memoryMB} MB`} />
              <Row label={isAr ? "البيئة" : "Environment"} value={d.application.environment} />
              {d.application.versionIsPlaceholder ? (
                <p className="mt-2 text-xs text-muted-foreground" data-testid="admin-health-version-note">
                  {isAr
                    ? "لا يوجد معرّف إصدار حقيقي؛ الرقم المعروض ثابت في الشيفرة."
                    : "No real build identifier is available; the served version is a hardcoded literal."}
                </p>
              ) : null}
            </Card>

            <Card title={isAr ? "المزوّدون" : "Providers"}>
              <p className="mb-2 text-xs text-muted-foreground" data-testid="admin-health-provider-note">
                {isAr
                  ? "لا تستدعي هذه الصفحة أي مزوّد. الأرقام مسجَّلة من استدعاءات حقيقية منذ بدء العملية."
                  : "This page calls no provider. Counts come from real calls since the process started."}
              </p>
              <ul className="space-y-0.5">
                {d.aiOperations.providers.map(p => (
                  <li key={p.provider} data-testid="admin-health-provider">
                    <Row
                      label={label(PROVIDER_TEXT, p.provider)}
                      value={
                        <span className={ACCENT[p.status] ?? ""}>
                          {label(OPSTATUS_TEXT, p.status)}
                          <span className="ms-2 text-muted-foreground">
                            {p.calls} {isAr ? "استدعاء" : "calls"}
                            {p.failures > 0 ? ` · ${p.failures} ${isAr ? "فشل" : "failed"}` : ""}
                          </span>
                        </span>
                      }
                    />
                  </li>
                ))}
              </ul>
            </Card>

            <Card title={isAr ? "ميزات الذكاء الاصطناعي" : "AI features"}>
              <p className="mb-2 text-xs text-muted-foreground" data-testid="admin-health-count-unit">
                {isAr
                  ? "تُحتسب الاستدعاءات كمحاولات مزوّد، لا كإجراءات مستخدم: قد ينتج إجراء واحد عدة محاولات."
                  : "Calls are provider attempts, not user actions. One action can make several."}
              </p>
              <ul className="space-y-0.5">
                {d.aiOperations.features
                  .filter(f => f.feature !== "unattributed" || f.calls > 0 || f.blocked > 0)
                  .map(f => (
                  <li key={f.feature} data-testid="admin-health-feature">
                    <Row
                      label={label(FEATURE_TEXT, f.feature)}
                      value={
                        <span className={ACCENT[f.status] ?? ""}>
                          {/* LIFECYCLE first, always present. A pilot lock is
                              authoritative and the surface cannot execute, so it
                              stands alone — pairing it with an operational word
                              would imply the feature is usable. */}
                          <span
                            className="text-muted-foreground"
                            data-testid="admin-health-feature-lifecycle"
                          >
                            {label(LIFECYCLE_TEXT, f.lifecycle)}
                          </span>
                          {f.lifecycle === "pilot_locked" ? null : (
                            <span data-testid="admin-health-feature-status">
                              {" · "}
                              {label(OPSTATUS_TEXT, operationalStatus(f.status))}
                            </span>
                          )}
                          <span className="ms-2 text-muted-foreground">
                            {f.providers.length > 0
                              ? `${f.providers.map(pv => t(PROVIDER_TEXT[pv], pv)).join(" + ")} · `
                              : ""}
                            {f.calls} {isAr ? "استدعاء" : "calls"}
                            {f.failures > 0 ? ` · ${f.failures} ${isAr ? "فشل" : "failed"}` : ""}
                            {f.blocked > 0 ? ` · ${f.blocked} ${isAr ? "محجوب" : "blocked"}` : ""}
                          </span>
                        </span>
                      }
                    />
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-muted-foreground" data-testid="admin-health-token-note">
                {isAr
                  ? "وحدات المعالجة تُؤخذ كما يُبلّغ عنها المزوّد فقط، ولا تُقدَّر أبداً."
                  : "Token counts are taken only as the provider reports them, never estimated."}
              </p>
            </Card>

            <Card title={isAr ? "غير متاح" : "Not available"}>
              <p className="mb-2 text-xs text-muted-foreground">
                {isAr
                  ? "لا يمكن لـ Receipt إنتاج هذه المقاييس اليوم، ولا تُقدَّر تخميناً."
                  : "Receipt cannot produce these today, and they are not estimated."}
              </p>
              <ul className="flex flex-wrap gap-1.5">
                {d.unavailable.map(u => (
                  <li
                    key={u}
                    data-testid="admin-health-unavailable-metric"
                    className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                  >
                    {label(UNAVAILABLE_TEXT, u)}
                  </li>
                ))}
              </ul>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
